import { type BillingInterval, SubscriptionTier } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { Button, Dialog, toast } from '@utima/ui';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { trpc } from '@/services/trpc';

import { useSubscriptionManager } from '../hooks/useSubscriptionManager';

interface ChangePlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentTier: SubscriptionTier;
  selectedTier: SubscriptionTier | null;
  organizationId: string;
  onSubscriptionUpdate: () => void;
  selectedInterval: BillingInterval;
}

export function ChangePlanDialog({
  isOpen,
  onClose,
  currentTier,
  selectedTier,
  organizationId,
  onSubscriptionUpdate,
  selectedInterval,
}: ChangePlanDialogProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();
  const { isLoading, updateSubscription, cancelSubscription, subscribe } =
    useSubscriptionManager(organizationId);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    'idle' | 'processing' | 'redirecting'
  >('idle');

  const handleConfirm = async () => {
    if (!selectedTier) return;

    if (selectedTier !== SubscriptionTier.Free) {
      try {
        setIsProcessingPayment(true);
        setPaymentStatus('processing');

        const billingInfo = await trpcUtils.billingInfo.getBillingInfo.fetch({
          organizationId,
        });
        let billingInfoIsComplete = false;
        if (billingInfo) {
          if (billingInfo.isCompany) {
            if (billingInfo.companyName && billingInfo.addressLine1) {
              billingInfoIsComplete = true;
            }
          } else {
            if (billingInfo.addressLine1) {
              billingInfoIsComplete = true;
            }
          }
        }

        if (!billingInfoIsComplete) {
          toast.info(
            t`Please complete your billing information before subscribing to a paid plan.`,
          );
          navigate({
            to: '/organizations/$organizationId/billing',
            params: { organizationId },
            search: {
              redirect: window.location.pathname + window.location.search,
              tier: selectedTier,
              interval: selectedInterval,
            },
          });
          onClose();
          setIsProcessingPayment(false);
          setPaymentStatus('idle');

          return;
        }
      } catch (error) {
        console.error('Failed to fetch billing information:', error);
        toast.error(t`Could not verify billing information. Please try again.`);
        setIsProcessingPayment(false);
        setPaymentStatus('idle');
        onClose();

        return;
      }
    }

    try {
      if (selectedTier === SubscriptionTier.Free) {
        await cancelSubscription();
        toast.success(t`Subscription scheduled for cancellation.`);
        onSubscriptionUpdate();
        onClose();
      } else if (currentTier === SubscriptionTier.Free) {
        setIsProcessingPayment(true);
        setPaymentStatus('processing');
        const successUrl = `${window.location.origin}${window.location.pathname}?success=true`;
        const cancelUrl = `${window.location.origin}${window.location.pathname}?canceled=true`;
        const result = await subscribe({
          tier: selectedTier,
          billingInterval: selectedInterval,
          successUrl,
          cancelUrl,
        });
        if (result.url) {
          setPaymentStatus('redirecting');
          window.location.href = result.url;
        } else {
          toast.error(t`Failed to initiate subscription checkout.`);
          setIsProcessingPayment(false);
          setPaymentStatus('idle');
          onClose();
        }
      } else {
        setIsProcessingPayment(true);
        setPaymentStatus('processing');
        const result = await updateSubscription({
          tier: selectedTier,
          billingInterval: selectedInterval,
        });

        if (result.billingPortalUrl) {
          setPaymentStatus('redirecting');
        } else {
          toast.success(t`Subscription updated successfully.`);
          onSubscriptionUpdate();
          onClose();
        }
      }
    } catch (error) {
      console.error('Failed to change subscription:', error);
      const errorMessage =
        typeof error === 'object' && error !== null && 'message' in error
          ? String(error.message)
          : '';

      toast.error(
        errorMessage || t`An error occurred while processing your request.`,
      );
      setIsProcessingPayment(false);
      setPaymentStatus('idle');
      if (
        !errorMessage.includes('checkout') &&
        !errorMessage.includes('StripePaymentAuthenticationRequiredError')
      ) {
        onClose();
      }
    }
  };

  if (!selectedTier) return null;

  const isDowngradingToFree = selectedTier === SubscriptionTier.Free;
  const isLoaderVisible = isLoading || isProcessingPayment;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open && paymentStatus === 'idle') {
          onClose();
        }
      }}
    >
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>
            {isDowngradingToFree ? (
              <Trans>Cancel Subscription</Trans>
            ) : (
              <Trans>Change Subscription Plan</Trans>
            )}
          </Dialog.Title>
          <Dialog.Description>
            {paymentStatus === 'redirecting' ? (
              <Trans>
                Payment authentication or confirmation required. You will be
                redirected shortly...
              </Trans>
            ) : paymentStatus === 'processing' ? (
              <Trans>Processing your request. Please wait...</Trans>
            ) : isDowngradingToFree ? (
              <Trans>
                You are about to cancel your current paid plan ({currentTier})
                and switch to the Free tier. Your subscription will be canceled
                immediately, and your usage limits will revert to the Free tier
                defaults. Any remaining prepaid balance will be kept. Are you
                sure?
              </Trans>
            ) : (
              <Trans>
                You are changing from the {currentTier} plan to the&nbsp;
                {selectedTier} plan.
                <br />- Your new plan and billing cycle will start&nbsp;
                <strong>immediately</strong>.
                <br />- You will be charged the <strong>full price</strong> for
                the {selectedTier} plan right away.
                <br />- Any unused allowance from your previous paid period will
                be <strong>added to your prepaid balance</strong>.
                <br />- Your monthly usage counter will be reset to zero.
              </Trans>
            )}
          </Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer>
          <Button
            data-cta='admin-change-plan-cancel-button'
            variant='secondary'
            onClick={onClose}
            disabled={isLoaderVisible}
            className={paymentStatus === 'idle' ? '' : 'hidden'}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            data-cta='admin-change-plan-confirm-button'
            onClick={handleConfirm}
            disabled={isLoaderVisible}
            className={paymentStatus === 'idle' ? '' : 'hidden'}
          >
            {isLoaderVisible && paymentStatus === 'processing' && (
              <Loader2 className='mr-2 size-4 animate-spin' />
            )}
            {isDowngradingToFree ? (
              <Trans>Confirm Cancellation</Trans>
            ) : (
              <Trans>Confirm Change</Trans>
            )}
          </Button>

          {paymentStatus !== 'idle' && (
            <div className='flex w-full items-center justify-center'>
              <Loader2 className='mr-2 size-6 animate-spin' />
              <span>
                {paymentStatus === 'redirecting' ? (
                  <Trans>Redirecting to complete payment...</Trans>
                ) : (
                  <Trans>Processing...</Trans>
                )}
              </span>
            </div>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
